import { formatSecondsToTimestamp } from "@memorang/helpers/common";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import Image from "../CustomImage";
import BottomActionsRow from "./BottomActionsRow";
import PlaybackControls from "./PlaybackControls";
import SeekBar from "./SeekBar";
import type { PodcastPlayerContentProps } from "./types";

const DesktopBottomPlayer = ({
	title,
	author,
	artworkUri,
	isPlaying,
	positionSeconds,
	durationSeconds,
	playbackSpeed,
	isBookmarked,
	isFirstTrack,
	isLastTrack,
	onPlayPause,
	onSkipPrevious,
	onSkipNext,
	onRewind,
	onForward,
	onSeek,
	onToggleBookmark,
	onChangeSpeed,
}: PodcastPlayerContentProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	return (
		<Box
			bottom={0}
			left={0}
			right={0}
			zIndex={1000}
			backgroundColor={theme.colors.surface}
			style={[
				styles.container,
				styles.shadow,
				styles.topBorder,
				{
					shadowColor: theme.colors.shadow,
					borderColor: theme.colors.outlineVariant,
				},
			]}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				paddingHorizontal={16}
				paddingVertical={12}
				gap={16}
				width="100%"
			>
				{/* Artwork + metadata */}
				<Box flexDirection="row" alignItems="center" gap={12} flex={1}>
					<Image
						source={{ uri: artworkUri }}
						accessibilityLabel={t("podcast.a11y.artwork", { title })}
						style={[styles.artwork, { borderRadius: theme.roundness * 3 }]}
						contentFit="cover"
					/>
					<Box flex={1} gap={2}>
						<Text
							variant="bodyMedium"
							numberOfLines={1}
							style={{ color: theme.colors.onSurface }}
						>
							{title}
						</Text>
						{!!author && (
							<Text
								variant="bodySmall"
								numberOfLines={1}
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{author}
							</Text>
						)}
					</Box>
				</Box>

				{/* Playback controls + seek bar */}
				<Box alignItems="center" width={520} gap={6}>
					<PlaybackControls
						isPlaying={isPlaying}
						isFirstTrack={isFirstTrack}
						isLastTrack={isLastTrack}
						compact
						onPlayPause={onPlayPause}
						onSkipPrevious={onSkipPrevious}
						onSkipNext={onSkipNext}
						onRewind={onRewind}
						onForward={onForward}
					/>
					<Box flexDirection="row" alignItems="center" gap={8} width="100%">
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{formatSecondsToTimestamp(positionSeconds)}
						</Text>
						<Box flex={1}>
							<SeekBar
								variant="compact"
								position={positionSeconds}
								duration={durationSeconds}
								onSeek={onSeek}
							/>
						</Box>
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{formatSecondsToTimestamp(durationSeconds)}
						</Text>
					</Box>
				</Box>

				{/* Actions */}
				<Box flex={1} alignItems="flex-end">
					<BottomActionsRow
						playbackSpeed={playbackSpeed}
						isBookmarked={isBookmarked}
						compact
						onChangeSpeed={onChangeSpeed}
						onToggleBookmark={onToggleBookmark}
					/>
				</Box>
			</Box>
		</Box>
	);
};

const styles = StyleSheet.create({
	container: {
		position: "fixed" as "absolute",
	},
	artwork: {
		width: 42,
		height: 42,
	},
	topBorder: {
		borderTopWidth: 1,
	},
	shadow: {
		shadowOffset: { width: 0, height: -2 },
		shadowOpacity: 0.08,
		shadowRadius: 8,
		elevation: 8,
	},
});

export default DesktopBottomPlayer;
